import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import { useTheme } from '@mui/material/styles';
import { getDpaAgreement, acceptDpa } from '../API/dpaApi';
import useAuth from '../hooks/useAuth';

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
`;

const replacePlaceholders = (text, user) => {
  if (!text || !user) return text;
  return text
    .replace(/\{\{companyName\}\}/g, user.companyName || '')
    .replace(/\{\{contactName\}\}/g, `${user.firstName || ''} ${user.lastName || ''}`.trim())
    .replace(/\{\{contactEmail\}\}/g, user.email || '');
};

const DpaAgreementModal = ({ open, onAccepted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const checkboxRef = useRef(null);

  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(null);
  const [highlightCheckbox, setHighlightCheckbox] = useState(false);

  useEffect(() => {
    if (open) {
      setChecked(false);
      setError(null);
      setHighlightCheckbox(false);
      setLoading(true);
      getDpaAgreement()
        .then((data) => {
          setAgreement(data);
          setLoading(false);
        })
        .catch(() => {
          setError('Kunde inte ladda avtalet. Försök igen senare.');
          setLoading(false);
        });
    }
  }, [open]);

  useEffect(() => {
    if (checked) {
      setHighlightCheckbox(false);
    }
  }, [checked]);

  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    getDpaAgreement()
      .then((data) => {
        setAgreement(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Kunde inte ladda avtalet. Försök igen senare.');
        setLoading(false);
      });
  }, []);

  const handleAccept = useCallback(async () => {
    if (!agreement) return;

    if (!checked) {
      setHighlightCheckbox(true);
      checkboxRef.current?.querySelector('input')?.focus();
      return;
    }

    setAccepting(true);
    setError(null);
    try {
      await acceptDpa(agreement.version);
      onAccepted?.();
    } catch (err) {
      setError(err?.data?.message || 'Något gick fel. Försök igen.');
    } finally {
      setAccepting(false);
    }
  }, [checked, agreement, onAccepted]);

  const displayedContent = agreement
    ? replacePlaceholders(agreement.content, user)
    : '';

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px',
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 2,
          px: 3,
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          Personuppgiftsbiträdesavtal
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Vänligen läs igenom och godkänn avtalet för att fortsätta använda Mina Sidor.
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 2,
          overflowY: 'auto',
        }}
      >
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Box py={4} textAlign="center">
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button
              onClick={handleRetry}
              variant="outlined"
              sx={{ textTransform: 'none', borderRadius: '8px' }}
            >
              Försök igen
            </Button>
          </Box>
        )}

        {!loading && !error && agreement && (
          <Box
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              fontSize: '14px',
              lineHeight: 1.7,
              color: theme.palette.text.primary,
            }}
          >
            {displayedContent}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FormControlLabel
          ref={checkboxRef}
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              disabled={accepting || loading || !!error}
              sx={{
                '&.Mui-checked': { color: '#006f97' },
                ...(highlightCheckbox && {
                  color: '#d32f2f',
                }),
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                ...(highlightCheckbox && {
                  color: '#d32f2f',
                  animation: `${shake} 0.4s ease-in-out`,
                }),
              }}
            >
              Jag har läst och godkänner Personuppgiftsbiträdesavtalet
            </Typography>
          }
          sx={{
            ...(highlightCheckbox && {
              backgroundColor: '#ffebee',
              borderRadius: '8px',
              mx: 0,
              px: 1,
              transition: 'background-color 0.3s ease',
            }),
          }}
        />

        <Box display="flex" justifyContent="flex-end">
          <Button
            onClick={handleAccept}
            variant="contained"
            disabled={accepting || loading || !!error}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              backgroundColor: '#006f97',
              px: 4,
              py: 1,
              fontWeight: 600,
              '&:hover': { backgroundColor: '#00B8B4' },
              '&:disabled': { backgroundColor: '#e0e0e0' },
            }}
          >
            {accepting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Godkänn och fortsätt'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DpaAgreementModal;
